import { useMemo } from 'react';
import Document from '@tiptap/extension-document';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import CodeBlockComponent from './tiptap-code-component';
import useLowlightOptions from './lowlight-options';
import Math from '@tiptap/extension-mathematics';
import { TableKit } from '@tiptap/extension-table';
import DocumentEmbed from './document-embed-extension';

const CustomDocument = Document.extend({
  content: 'codeBlock'
});

const useExtensions = ({ onlyCode, textPlaceholder }) => {
  const lowlightSyntaxHighlighting = useLowlightOptions();

  const extensions = useMemo(
    () => [
      ...(onlyCode
        ? [CustomDocument, StarterKit.configure({ codeBlock: false, document: false })]
        : [StarterKit.configure({ codeBlock: false })]),
      Placeholder.configure({ placeholder: textPlaceholder }),
      Math,
      ...(!onlyCode
        ? [
            DocumentEmbed,
            TableKit.configure({
              table: {
                resizable: false,
                HTMLAttributes: {
                  class: 'tiptap-table'
                }
              }
            })
          ]
        : []),
      CodeBlockLowlight.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeBlockComponent);
        },
        addKeyboardShortcuts() {
          return {
            // Tab in code block should insert 4 spaces
            Tab: () => {
              if (this.editor.isActive('codeBlock')) {
                return this.editor.commands.insertContent('    ');
              }
            },
            // Enter → auto-indent based on previous line
            Enter: () => {
              if (!this.editor.isActive('codeBlock')) return false;

              const { state } = this.editor;
              const { selection } = state;
              const { $from } = selection;

              // Get the text inside the code block up to the cursor
              const parentText = $from.parent.textContent;
              const beforeCursor = parentText.slice(0, $from.parentOffset);
              const afterCursor = parentText.slice($from.parentOffset);

              // Get the previous line (split on \n)
              const prevLine = beforeCursor.split('\n').pop() || '';

              /*
                LOGIC:
                If we're not in between braces:
                  - Newline + indent
                  - Cursor at $from.pos + indent.length + 1
                If we're in between braces - what is the content we need to add?
                  - Newline
                  - Indent + Extra 4 spaces
                  - Newline + indent
              */
              // Match leading whitespace
              const indentMatch = prevLine.match(/^\s+/);
              const indent = indentMatch ? indentMatch[0] : '';
              const inBetweenBraces =
                prevLine.trim().endsWith('{') && afterCursor.charAt(0) === '}';

              if (!inBetweenBraces) {
                return this.editor
                  .chain()
                  .insertContentAt($from.pos, '\n' + indent)
                  .run();
              } else {
                const content = `\n${indent}    \n${indent}`;
                const cursor = $from.pos + indent.length + 5; // +5 for the 4 spaces and new line
                return this.editor
                  .chain()
                  .insertContentAt($from.pos, content)
                  .setTextSelection(cursor)
                  .run();
              }
            }
          };
        }
      }).configure({
        lowlight: lowlightSyntaxHighlighting
      })
    ],
    [lowlightSyntaxHighlighting, onlyCode, textPlaceholder]
  );

  return extensions;
};

export default useExtensions;
